import React, { useState, memo } from 'react';
import { useSelector } from 'react-redux';
import { useForm, SubmitHandler } from 'react-hook-form';
import moment from 'moment-timezone';

import {
	Typography,
	Grid,
	Box,
	TextField,
	Button,
	InputAdornment,
	IconButton,
} from '@mui/material';
import { ReactComponent as MaskedIcon } from 'common/assets/icons/icon-masked-outline.svg';
import { ReactComponent as UnmaskedIcon } from 'common/assets/icons/icon-unmasked-outline.svg';
import ContainerBox from 'components/ContainerBox';
import Spinner from 'components/Spinner';
import SkidataLiftTicketSegments from './SkidataLiftTicketSegments';

import useShopFormat from 'hooks/useShopFormat';
import { SkidataCredentials } from 'common/modules/skidata';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useTranslation } from 'services/localization/useTranslation';
import { Callable } from 'common/frontend/callable';
import errorHandler from 'common/services/errorHandler';
import Banner from 'components/Banner';

const Skidata = () => {
	const { localFormat } = useShopFormat();
	const { t } = useTranslation();

	const skidataInfo = useSelector(ShopSelectors.skidataInfo);
	const shopId = useSelector(ShopSelectors.activeShopId);

	const [loading, setLoading] = useState<boolean>(false);
	const [formError, setFormError] = useState<string>('');
	const [syncSuccessText, setSyncSuccessText] = useState<string>('');
	const [syncFailedText, setSyncFailedText] = useState<string>('');
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [isUpdatingLiftTickets, setIsUpdatingLiftTickets] = useState(false);

	const { register, handleSubmit, formState } = useForm<SkidataCredentials>({
		defaultValues: {
			skidataClientName: skidataInfo?.skidataClientName ?? '',
			skidataUsername: skidataInfo?.skidataUsername ?? '',
			skidataPassword: skidataInfo?.skidataPassword ?? '',
		},
		mode: 'onChange',
	});
	const { errors } = formState;

	if (!shopId) return null;

	const handleSubmitForm: SubmitHandler<SkidataCredentials> = async (data) => {
		if (!formState.isValid) {
			return;
		}
		setFormError('');
		setLoading(true);
		try {
			await Callable.skidata.activate({ shopId, credentials: data });
		} catch (err) {
			setFormError(t('skidata.invalidCredentials', 'Invalid Skidata credentials') + '!');
		}
		setLoading(false);
	};

	const handleSynchronizeCatalog = async () => {
		setFormError('');
		setIsUpdatingLiftTickets(true);
		try {
			const { liftTicketProducts, invalidTicketProducts } = await Callable.skidata.catalogue.sync({
				shopId,
			});
			if (liftTicketProducts && liftTicketProducts.length > 0) {
				setSyncSuccessText(
					t('skidata.synchronizeProductsSuccessText', {
						numOfProducts: liftTicketProducts.length,
						defaultValue: `Succesfully synchronized {{numOfProducts}} ticket products`,
					}),
				);
			}
			if (invalidTicketProducts && invalidTicketProducts.length > 0) {
				setSyncFailedText(
					t('skidata.synchronizeProductsFailedText', {
						numOfProducts: invalidTicketProducts.length,
						defaultValue: `Failed to synchronize {{numOfProducts}} ticket products: KeyCards must be set as additional products in catalog. Contact SKIDATA for more information.`,
					}),
				);
			}
		} catch (e) {
			setFormError(t('skidata.synchronizeCatalogFailed', 'Synchronize catalog failed') + '!');
		}
		setIsUpdatingLiftTickets(false);
	};

	const handleDisableSkidata = async () => {
		setLoading(true);
		try {
			await Callable.skidata.disable({ shopId });
		} catch (err) {
			errorHandler.report(err);
		}
		setLoading(false);
	};

	const renderClientName = () => (
		<Grid item xs={12}>
			<TextField
				label={t('skidata.skidataClientName', 'Client name')}
				variant="standard"
				margin="dense"
				type="text"
				fullWidth
				error={!!errors.skidataClientName}
				helperText={errors.skidataClientName?.message}
				disabled={!!skidataInfo?.skidataClientName}
				inputProps={{
					readOnly: !!skidataInfo,
				}}
				{...register('skidataClientName', {
					required: t('common:validation.cannotBeEmpty', {
						field: t('skidata.skidataClientName', 'Client name'),
					}),
				})}
			/>
		</Grid>
	);

	const renderUsername = () => (
		<Grid item xs={12} sm={6}>
			<TextField
				label={t('common:form.username', 'Username')}
				variant="standard"
				margin="dense"
				type="text"
				fullWidth
				error={!!errors.skidataUsername}
				helperText={errors.skidataUsername?.message}
				disabled={!!skidataInfo?.skidataUsername}
				inputProps={{
					readOnly: !!skidataInfo,
				}}
				{...register('skidataUsername', {
					required: t('common:validation.cannotBeEmpty', {
						field: t('common:form.username', 'Username'),
					}),
				})}
			/>
		</Grid>
	);

	const renderPassword = () => (
		<Grid item xs={12} sm={6}>
			<TextField
				label={t('common:form.password')}
				variant="standard"
				margin="dense"
				autoComplete="new-password"
				type={showPassword ? 'text' : 'password'}
				fullWidth
				error={!!errors.skidataPassword}
				helperText={errors.skidataPassword?.message}
				disabled={!!skidataInfo?.skidataPassword}
				inputProps={{
					readOnly: !!skidataInfo,
				}}
				{...register('skidataPassword', {
					required: t('common:validation.cannotBeEmpty', { field: t('common:form.password') }),
				})}
				InputProps={{
					endAdornment: (
						<InputAdornment position="end">
							<IconButton
								aria-label="toggle password visibility"
								onClick={() => setShowPassword(!showPassword)}
								edge="end"
								size="small"
								color="secondary"
							>
								{showPassword ? <MaskedIcon width={24} /> : <UnmaskedIcon width={24} />}
							</IconButton>
						</InputAdornment>
					),
				}}
			/>
		</Grid>
	);

	const renderActions = () => (
		<>
			{skidataInfo && (
				<>
					<Banner
						mt={3}
						variant="warning"
						title={t(
							'skidata.synchronizeCatalogAlertTitle',
							'Catalog synchronization refreshes ticket information',
						)}
						description={
							<Box>
								<Box>
									{t(
										'skidata.synchronizeCatalogAlertText1',
										'Make sure you have synchronized your SKIDATA catalog to have correct lift tickets’ details on the Products view',
									)}
									.
								</Box>
								<Box mt={2}>
									{t(
										'skidata.synchronizeCatalogAlertText3',
										'After synchronizing catalog, some segments might be added or removed, the changes affect only lift tickets products and do not change either other products or orders',
									)}
									.
								</Box>
							</Box>
						}
					/>
					{syncSuccessText && <Banner mt={3} variant="success" title={syncSuccessText} />}
					{syncFailedText && <Banner mt={3} variant="warning" title={syncFailedText} />}
					<Box mt={3}>
						<Button
							variant="contained"
							color="primary"
							disabled={isUpdatingLiftTickets}
							onClick={handleSynchronizeCatalog}
						>
							{isUpdatingLiftTickets ? (
								<Spinner padding={8} thickness={4} color="white" />
							) : (
								t('skidata.synchronizeCatalog', 'Synchronize catalog')
							)}
						</Button>
					</Box>
					<Box mt={1}>
						<Typography variant="body2">
							{skidataInfo.lastSynced
								? `${t('skidata.lastSynced', 'Last synced')}: ${localFormat(
										moment(skidataInfo.lastSynced),
										'DD.MM.YYYY, HH:mm',
								  )}`
								: t('skidata.neverSyncedBefore', 'Never synced before')}
							.
						</Typography>
					</Box>
				</>
			)}
			<Box mt={3}>
				<Button
					variant={skidataInfo ? 'outlined' : 'contained'}
					color="primary"
					onClick={skidataInfo ? handleDisableSkidata : handleSubmit(handleSubmitForm)}
					disabled={loading || !formState.isValid}
				>
					{loading ? (
						<Spinner padding={8} thickness={4} color="inherit" />
					) : skidataInfo ? (
						t('skidata.disableSkidata', 'Log out and disable Skidata')
					) : (
						t('skidata.loginAndActivate', 'Log in and activate Skidata')
					)}
				</Button>
			</Box>
		</>
	);

	return (
		<ContainerBox>
			<Typography variant="h5">{t('skidata.header', 'Skidata')}</Typography>
			<Typography variant="body2" color="textSecondary" gutterBottom>
				{t('skidata.headerDescription', 'Get access to your Skidata lift ticket catalog.')}
			</Typography>
			<Box mt={2} />
			<Typography variant="body1" gutterBottom>
				{t('skidata.credentialsProvided', 'Credentials provided by Skidata')}
			</Typography>
			<Box maxWidth={652}>
				<Grid container spacing={3}>
					{renderClientName()}
					{renderUsername()}
					{renderPassword()}
				</Grid>
			</Box>
			{renderActions()}
			{formError && <Banner mt={3} variant="danger" title={formError} />}
			{skidataInfo && <SkidataLiftTicketSegments />}
		</ContainerBox>
	);
};

export default memo(Skidata);
